import React, { useMemo } from "react";
import { ButtonProps, Form } from "antd";
import OrderTag from "@/enum/OrderTag";
import OrderTagNameMap from "@/map/OrderTagNameMap";
import ShopType from "@/enum/ShopType";
import { encryptedManualOrderTags } from "@/utils/OrderTagUtils";
import { CollapsibleGroupSelect, OptionItem } from "@/components/CollapsibleGroupSelect";
import { Select } from "../BaseSelect";

// 构建过滤并格式化标签列表
function getOrderTagsDataSource(shopType?: ShopType) {
    return Object.keys(OrderTag)
        .map((key: OrderTag) => {
            const value = OrderTag[key];

            // 以下标签不展示
            if (
                key === OrderTag.LOCAL_DOUBLE_ADDRESS ||
                ([OrderTag.DOUDIAN_DELIVERY_APPOINTMENT, OrderTag.DOUDIAN_APPOINTMENT_ARRIVAL] as string[]).includes(
                    key,
                ) ||
                (key === OrderTag.CONSOLIDATED_SHIPPING &&
                    ![ShopType.PINDUODUO, ShopType.PDD_FDS, ShopType.PDD_FULFILLMENT].includes(shopType!))
            ) {
                return null;
            }

            // 后续移除标签移除此处代码
            // 需要同步修改src\components\CollapsibleGroupSelect\index.tsx中的SelectedTagsPopoverContent展示逻辑
            if (
                [
                    OrderTag.ALI_CAN_NOT_SHIP,
                    OrderTag.TAOTIAN_PAID_FORBIN_CONSIGN,
                    /** 旧顺丰标签 */
                    OrderTag.TAOTIAN_SHIP_BY_SF,
                    OrderTag.PDD_SHIP_BY_SF,
                    OrderTag.DOUDIAN_SHIP_BY_SF,
                    OrderTag.KWAI_SHIP_BY_SF,
                    OrderTag.DOUDIAN_PAID_SF_EXPRESS,
                    OrderTag.DOUDIAN_PAID_SF_EXPRESS_TK,
                ].includes(key)
            ) {
                return null;
            }

            // 跨平台同名添加前缀
            if (!shopType) {
                const prefixMap: Record<string, string> = {
                    [OrderTag.KWAI_PRIORITY_DELIVERY]: "(快手)",
                    [OrderTag.DOUDIAN_PRIORITY_DELIVERY]: "(抖店)",
                    [OrderTag.KWAI_CONSOLIDATION_XINJIANG]: "(快手)",
                    [OrderTag.DOUDIAN_CONSOLIDATION_XINJIANG]: "(抖店)",
                    [OrderTag.JD_CONSOLIDATION_XINJIANG]: "(京东)",
                    [OrderTag.TAOTIAN_XINJIANG_CONSOLIDATE]: "(淘宝)",
                    [OrderTag.DOUDIAN_SHIP_BY_SF]: "(抖店)",
                    [OrderTag.KWAI_SHIP_BY_SF]: "(快手)",
                    [OrderTag.KWAI_CONSOLIDATION_NEIMENGGU]: "(快手)",
                    [OrderTag.TAOTIAN_CONSOLIDATED_SHIPPING]: "(淘宝)",
                    [OrderTag.KWAI_TOMORROW_ARRIVAL]: "(快手)",
                    [OrderTag.DOUDIAN_TOMORROW_RECEIPT]: "(抖店)",
                };
                if (prefixMap[key]) {
                    return { value, label: `${OrderTagNameMap[key]}${prefixMap[key]}` };
                }
            }

            return { value, label: OrderTagNameMap[key] };
        })
        .filter(Boolean) as OptionItem<OrderTag>[];
}

function getFiletedOrderTags(shopType?: ShopType) {
    if (!shopType) return getOrderTagsDataSource();

    const doudianShops = [
        ShopType.DOUDIAN,
        ShopType.DOUDIAN_SUPPLIER,
        ShopType.DOUDIAN_DROP_SHIPPING_SUPPLIER,
        ShopType.DOUDIAN_IOP,
    ];
    const kwaiShops = [ShopType.KWAI, ShopType.KWAI_DROPSHIP];
    const taobaoShops = [ShopType.TAOBAO, ShopType.TAOBAO_DISTRIBUTION, ShopType.TAOBAO_DROP_SHIPPING_SUPPLIER];
    const pddShops = [
        ShopType.PINDUODUO,
        ShopType.PDD_DROP_SHIPPING_SUPPLIER,
        ShopType.PDD_FDS,
        ShopType.PDD_FULFILLMENT,
    ];
    const jdShops = [ShopType.JD, ShopType.JD_PURCHASE, ShopType.JD_DROP_SHIPPING_SUPPLIER];
    const channelShops = [ShopType.CHANNEL_SHOP, ShopType.CHANNEL_SHOP_DROPSHIP, ShopType.CHANNEL_SHOP_SUPPLIER];
    const otherDropShippingShops = [ShopType.OTHER_DROP_SHIPPING_SUPPLIER];

    return getOrderTagsDataSource(shopType).filter((it) => {
        const val = it.value;

        // 手工店铺平台加密订单标签数组提前处理
        if (encryptedManualOrderTags.includes(val)) {
            return shopType === ShopType.MANUAL;
        }

        // —— 全部常量标签提前 switch 处理 ——
        switch (val) {
            // 永远过滤双地址
            case OrderTag.LOCAL_DOUBLE_ADDRESS:
                return false;

            // 旧标签（兼容时期，后续可以删除该代码）
            case OrderTag.ALI_CAN_NOT_SHIP:
            case OrderTag.TAOTIAN_PAID_FORBIN_CONSIGN:
                return false;

            case OrderTag.SAMPLE:
                // 样品：抖店
                return doudianShops.includes(shopType);

            case OrderTag.GREETING_CARD:
                // 礼物贺卡：微信小店
                return shopType === ShopType.CHANNEL_SHOP;

            case OrderTag.ORDER_ITEM_PART_BUNDLE_SKU_SHIPPED:
            case OrderTag.SHIPPING_DELAY:
                // 组合品部分发货、推迟发货：抖店
                return doudianShops.includes(shopType);

            case OrderTag.LUCKY_MONEY:
                // 红包：淘宝
                return [ShopType.TAOBAO, ShopType.TAOBAO_DISTRIBUTION].includes(shopType);

            case OrderTag.SUBSIDY_SUPPLY_SALE:
                // 国补供销：快手
                return kwaiShops.includes(shopType) || otherDropShippingShops.includes(shopType);

            case OrderTag.SUBSIDY_MAJOR_APPLIANCE:
                // 国补大家电：抖店
                return doudianShops.includes(shopType);

            case OrderTag.FREE_SF:
                // 顺丰包邮：通用，但抖店不展示
                return !doudianShops.includes(shopType);

            case OrderTag.PRESALE:
                // 预售：通用，但抖店不展示
                return !doudianShops.includes(shopType);

            case OrderTag.DOUBLE_ADDRESS:
                // 双地址：只给抖店
                return doudianShops.includes(shopType);

            case OrderTag.EXPEDITE_DELIVERY:
                // 催发货
                return [
                    ShopType.TAOBAO,
                    ShopType.TAOBAO_DROP_SHIPPING_SUPPLIER,
                    ShopType.PINDUODUO,
                    ShopType.PDD_DROP_SHIPPING_SUPPLIER,
                    ShopType.PDD_FDS,
                    ShopType.PDD_FULFILLMENT,
                    ShopType.KWAI,
                    ShopType.KWAI_DROPSHIP,
                    ShopType.DOUDIAN,
                    ShopType.XIAOHONGSHU,
                    ShopType.OTHER_DROP_SHIPPING_SUPPLIER,
                ].includes(shopType);

            default:
                break;
        }

        // 根据前缀划分平台
        if (val.startsWith("TMALL_") || val.startsWith("TB_") || val.startsWith("TAOTIAN")) {
            return taobaoShops.includes(shopType);
        }
        if (val.startsWith("ALI_C2M")) {
            return shopType === ShopType.ALI_C2M || shopType === ShopType.TAOBAO_DROP_SHIPPING_SUPPLIER;
        }
        if (val.startsWith("ALI_")) {
            return (
                shopType === ShopType.ALIBABA ||
                shopType === ShopType.ALIBABA_SUPPLIER ||
                shopType === ShopType.TAOBAO_DROP_SHIPPING_SUPPLIER
            );
        }
        if (val.startsWith("JD_")) {
            return jdShops.includes(shopType);
        }
        if (val.startsWith("PDD_")) {
            return pddShops.includes(shopType);
        }
        if (val.startsWith("KWAI_")) {
            return kwaiShops.includes(shopType) || otherDropShippingShops.includes(shopType);
        }
        if (val.startsWith("CHANNELSHOP_")) {
            return channelShops.includes(shopType);
        }
        if (val.startsWith("JL_")) {
            return shopType === ShopType.WEBUY || otherDropShippingShops.includes(shopType);
        }
        if (val.startsWith("XHS_")) {
            return shopType === ShopType.XIAOHONGSHU || otherDropShippingShops.includes(shopType);
        }
        if (val.startsWith("DOUDIAN_")) {
            return doudianShops.includes(shopType);
        }

        return true;
    });
}

// 构建分组规则工厂
function buildGroupRule(rules: { prefixes: string | string[]; label: string }[], defaultLabel = "通用") {
    return (item: OptionItem<OrderTag>) => {
        const val = item.value;
        for (const { prefixes, label } of rules) {
            const list = Array.isArray(prefixes) ? prefixes : [prefixes];
            if (list.some((p) => val.startsWith(p) || val === p)) return label;
        }
        return defaultLabel;
    };
}

const groupingConfig = {
    [ShopType.TAOBAO_DROP_SHIPPING_SUPPLIER]: [
        { prefixes: ["TMALL_", "TB_", "TAOTIAN", OrderTag.LUCKY_MONEY], label: "淘宝" },
        { prefixes: "ALI_C2M", label: "淘工厂" },
        { prefixes: "ALI_", label: "1688" },
    ],
    [ShopType.OTHER_DROP_SHIPPING_SUPPLIER]: [
        {
            prefixes: ["KWAI_", OrderTag.DOUBLE_ADDRESS, OrderTag.SUBSIDY_SUPPLY_SALE],
            label: "快手",
        },
        { prefixes: "JL_", label: "鲸灵" },
        {
            prefixes: ["XHS_", OrderTag.EXPEDITE_DELIVERY],
            label: "小红书",
        },
    ],
    [ShopType.TAOBAO]: [
        {
            prefixes: ["TMALL_", "TB_", "TAOTIAN", OrderTag.LUCKY_MONEY],
            label: "淘宝",
        },
    ],
    [ShopType.ALIBABA]: [
        {
            prefixes: ["ALI_"],
            label: "阿里巴巴",
        },
    ],
    [ShopType.ALIBABA_SUPPLIER]: [
        {
            prefixes: ["ALI_"],
            label: "阿里巴巴",
        },
    ],
    [ShopType.ALI_C2M]: [
        {
            prefixes: ["ALI_C2M"],
            label: "淘工厂",
        },
    ],
    [ShopType.JD]: [
        {
            prefixes: ["JD_"],
            label: "京东",
        },
    ],
    [ShopType.JD_DROP_SHIPPING_SUPPLIER]: [
        {
            prefixes: ["JD_"],
            label: "京东",
        },
    ],
    [ShopType.DOUDIAN]: [
        {
            prefixes: [
                "DOUDIAN_",
                OrderTag.DOUBLE_ADDRESS,
                OrderTag.SAMPLE,
                OrderTag.ORDER_ITEM_PART_BUNDLE_SKU_SHIPPED,
                OrderTag.SHIPPING_DELAY,
                OrderTag.SUBSIDY_MAJOR_APPLIANCE,
            ],
            label: "抖店",
        },
    ],
    [ShopType.DOUDIAN_DROP_SHIPPING_SUPPLIER]: [
        {
            prefixes: [
                "DOUDIAN_",
                OrderTag.DOUBLE_ADDRESS,
                OrderTag.SAMPLE,
                OrderTag.ORDER_ITEM_PART_BUNDLE_SKU_SHIPPED,
                OrderTag.SHIPPING_DELAY,
                OrderTag.SUBSIDY_MAJOR_APPLIANCE,
            ],
            label: "抖店",
        },
    ],
    [ShopType.KWAI]: [
        {
            prefixes: ["KWAI_", OrderTag.DOUBLE_ADDRESS, OrderTag.SUBSIDY_SUPPLY_SALE],
            label: "快手",
        },
    ],
    [ShopType.WEBUY]: [{ prefixes: "JL_", label: "鲸灵" }],
    [ShopType.CHANNEL_SHOP]: [
        {
            prefixes: ["CHANNELSHOP_", OrderTag.GREETING_CARD],
            label: "微信小店",
        },
    ],
    [ShopType.XIAOHONGSHU]: [
        {
            prefixes: ["XHS_", OrderTag.EXPEDITE_DELIVERY],
            label: "小红书",
        },
    ],
    [ShopType.MANUAL]: [{ prefixes: encryptedManualOrderTags, label: "手工店铺" }],
    [ShopType.INDEPENDENT_MANUAL]: [{ prefixes: encryptedManualOrderTags, label: "手工店铺" }],
};

// 默认跨平台分组
const defaultGroupRule = buildGroupRule([
    { prefixes: ["TMALL_", "TB_", "ALI_C2M", "ALI_", "TAOTIAN", OrderTag.LUCKY_MONEY], label: "淘系" },
    { prefixes: ["JD_"], label: "京东" },
    {
        prefixes: [
            "DOUDIAN_",
            OrderTag.DOUBLE_ADDRESS,
            OrderTag.SAMPLE,
            OrderTag.ORDER_ITEM_PART_BUNDLE_SKU_SHIPPED,
            OrderTag.SHIPPING_DELAY,
            OrderTag.SUBSIDY_MAJOR_APPLIANCE,
        ],
        label: "抖店",
    },
    {
        prefixes: ["KWAI_", OrderTag.SUBSIDY_SUPPLY_SALE],
        label: "快手",
    },
    { prefixes: "JL_", label: "鲸灵" },
    { prefixes: ["CHANNELSHOP_", "XHS_", OrderTag.GREETING_CARD, OrderTag.EXPEDITE_DELIVERY], label: "微信小店小红书" },
    { prefixes: encryptedManualOrderTags, label: "手工店铺" },
]);

interface OrderTagSelectProps {
    shopType?: ShopType;
    onChange?: (value: OrderTag[]) => void;
    value?: OrderTag[];
}

/** 订单标签选择组件 */
const OrderTagSelect: React.FC<OrderTagSelectProps> = (props) => {
    const { shopType } = props;
    const data = useMemo(() => getFiletedOrderTags(shopType), [shopType]);
    const groupBy = useMemo(() => {
        const config = shopType ? groupingConfig[shopType] : null;
        return config ? buildGroupRule(config) : defaultGroupRule;
    }, [shopType]);

    return (
        <CollapsibleGroupSelect
            size="small"
            data={data}
            groupBy={groupBy}
            Component={Select}
            onChange={props.onChange}
            value={props.value}
        />
    );
};

export default React.memo(OrderTagSelect);
